import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Tooltip,
  Popover,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Notifications as NotificationsIcon, Warning as WarningIcon } from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';
import NotificationList from '../components/NotificationList';

interface NotificationBellProps {
  color?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ color = 'inherit' }) => {
  const { notifications, unreadCount, markAllAsRead, isLoading, error } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notifications-popover' : undefined;

  return (
    <>
      <Tooltip title={error ? "Notification error - click to retry" : "Notifications"}>
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          color={color as any}
          size="large"
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : error ? (
            <Badge badgeContent="!" color="error" overlap="circular">
              <NotificationsIcon color="action" />
            </Badge>
          ) : (
            <Badge badgeContent={unreadCount} color="error" overlap="circular">
              <NotificationsIcon />
            </Badge>
          )}
        </IconButton>
      </Tooltip>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            maxHeight: 500,
            mt: 1.5
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Notifications
              {!error && unreadCount > 0 && (
                <Typography 
                  component="span" 
                  sx={{ 
                    backgroundColor: 'error.main', 
                    color: 'white', 
                    borderRadius: 10, 
                    px: 1, 
                    py: 0.5, 
                    ml: 1,
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  {unreadCount} new
                </Typography>
              )}
            </Typography>
            
            {!error && unreadCount > 0 && (
              <Button 
                size="small"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </Button>
            )}
            
            {error && (
              <Button 
                size="small"
                onClick={handleRetry}
                startIcon={<WarningIcon fontSize="small" />}
                color="error"
              >
                Retry
              </Button>
            )}
          </Box>
        </Box>
        
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This could be due to network issues or missing permissions. Your notifications will appear here once the issue is resolved.
            </Typography>
          </Box>
        )}
        
        {!error && <NotificationList onNotificationClick={handleClose} />}
      </Popover>
    </>
  );
};

export default NotificationBell; 